import Cookies from 'js-cookie';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_SETTINGS_KEY = 'cookie_settings';

// Cookie kulcsok konstansai
const COOKIE_KEYS = {
    // Funkcionális cookie-k
    THEME: 'theme_preference',
    LANGUAGE: 'language_preference',
    FONT_SIZE: 'font_size_preference',
    NOTIFICATION_SETTINGS: 'notification_settings',
    LAST_VISITED: 'last_visited_page',
    
    // Analitikai cookie-k
    VISIT_COUNT: 'visit_count',
    FIRST_VISIT: 'first_visit_date',
    PAGE_VIEWS: 'page_views',
    REFERRER: 'referrer_source',
    
    // Marketing cookie-k
    CAMPAIGN_SOURCE: 'utm_source',
    CAMPAIGN_MEDIUM: 'utm_medium',
    CAMPAIGN_NAME: 'utm_campaign',
    
    // Szükséges cookie-k
    SESSION_ID: 'session_id',
    AUTH_TOKEN: 'auth_token',
    CSRF_TOKEN: 'csrf_token'
};

class CookieService {
    static getDefaultSettings() {
        return {
            necessary: true, // Mindig engedélyezve
            analytics: false,
            marketing: false,
            functional: false,
        };
    }

    static getConsentStatus() {
        return Cookies.get(COOKIE_CONSENT_KEY);
    }

    static getCookieSettings() {
        const settings = Cookies.get(COOKIE_SETTINGS_KEY);
        return settings ? JSON.parse(settings) : this.getDefaultSettings();
    }

    static setConsentStatus(status) {
        Cookies.set(COOKIE_CONSENT_KEY, status, { expires: 365 });
    }

    static setCookieSettings(settings) {
        Cookies.set(COOKIE_SETTINGS_KEY, JSON.stringify(settings), { expires: 365 });
        this.setConsentStatus('custom');
        this.applyCookieSettings(settings);
    }

    static acceptAllCookies() {
        const settings = {
            necessary: true,
            analytics: true,
            marketing: true,
            functional: true,
        };
        this.setCookieSettings(settings);
        this.setConsentStatus('accepted');
        this.initializeAllCookies();
    }

    static rejectAllCookies() {
        const settings = this.getDefaultSettings();
        this.setCookieSettings(settings);
        this.setConsentStatus('rejected');
        this.deleteCookies();
    }

    // Felhasználói preferenciák kezelése
    static setUserPreference(key, value) {
        const settings = this.getCookieSettings();
        if (settings.functional) {
            Cookies.set(key, JSON.stringify(value), { expires: 365 });
            return true;
        }
        return false;
    }

    static getUserPreference(key) {
        const settings = this.getCookieSettings();
        if (settings.functional) {
            const value = Cookies.get(key);
            return value ? JSON.parse(value) : null;
        }
        return null;
    }

    // Téma beállítások
    static setThemePreference(theme) {
        return this.setUserPreference(COOKIE_KEYS.THEME, theme);
    }

    static getThemePreference() {
        return this.getUserPreference(COOKIE_KEYS.THEME) || 'light';
    }

    // Nyelvi beállítások
    static setLanguagePreference(language) {
        return this.setUserPreference(COOKIE_KEYS.LANGUAGE, language);
    }

    static getLanguagePreference() {
        return this.getUserPreference(COOKIE_KEYS.LANGUAGE) || 'hu';
    }

    // Analitikai funkciók
    static trackPageView(pageUrl) {
        const settings = this.getCookieSettings();
        if (settings.analytics) {
            // Látogatások számolása
            const pageViews = this.getUserPreference(COOKIE_KEYS.PAGE_VIEWS) || {};
            pageViews[pageUrl] = (pageViews[pageUrl] || 0) + 1;
            this.setUserPreference(COOKIE_KEYS.PAGE_VIEWS, pageViews);

            // Utolsó látogatott oldal mentése
            this.setUserPreference(COOKIE_KEYS.LAST_VISITED, pageUrl);
        }
    }

    static initializeAnalytics() {
        const settings = this.getCookieSettings();
        if (settings.analytics) {
            // Első látogatás dátumának mentése
            if (!this.getUserPreference(COOKIE_KEYS.FIRST_VISIT)) {
                this.setUserPreference(COOKIE_KEYS.FIRST_VISIT, new Date().toISOString());
            }

            // Látogatás számláló növelése
            const visits = (this.getUserPreference(COOKIE_KEYS.VISIT_COUNT) || 0) + 1;
            this.setUserPreference(COOKIE_KEYS.VISIT_COUNT, visits);

            // Referrer mentése
            if (document.referrer) {
                this.setUserPreference(COOKIE_KEYS.REFERRER, document.referrer);
            }
        }
    }

    // Marketing funkciók
    static trackCampaign() {
        const settings = this.getCookieSettings();
        if (settings.marketing) {
            const urlParams = new URLSearchParams(window.location.search);
            const utmSource = urlParams.get('utm_source');
            const utmMedium = urlParams.get('utm_medium');
            const utmCampaign = urlParams.get('utm_campaign');

            if (utmSource) this.setUserPreference(COOKIE_KEYS.CAMPAIGN_SOURCE, utmSource);
            if (utmMedium) this.setUserPreference(COOKIE_KEYS.CAMPAIGN_MEDIUM, utmMedium);
            if (utmCampaign) this.setUserPreference(COOKIE_KEYS.CAMPAIGN_NAME, utmCampaign);
        }
    }

    // Authentikáció
    static setAuthToken(token) {
        // Szükséges cookie-k mindig engedélyezve vannak
        Cookies.set(COOKIE_KEYS.AUTH_TOKEN, token, {
            expires: 7,
            secure: true,
            sameSite: 'strict'
        });
    }

    static getAuthToken() {
        return Cookies.get(COOKIE_KEYS.AUTH_TOKEN);
    }

    static removeAuthToken() {
        Cookies.remove(COOKIE_KEYS.AUTH_TOKEN);
    }

    // Session kezelés
    static initializeSession() {
        if (!this.getUserPreference(COOKIE_KEYS.SESSION_ID)) {
            const sessionId = Math.random().toString(36).substring(2);
            this.setUserPreference(COOKIE_KEYS.SESSION_ID, sessionId);
        }
    }

    // Cookie-k inicializálása
    static initializeAllCookies() {
        const settings = this.getCookieSettings();
        
        // Szükséges cookie-k
        this.initializeSession();

        // Analitikai cookie-k
        if (settings.analytics) {
            this.initializeAnalytics();
        }

        // Marketing cookie-k
        if (settings.marketing) {
            this.trackCampaign();
        }

        // Funkcionális cookie-k alapértékei
        if (settings.functional) {
            if (!this.getThemePreference()) this.setThemePreference('light');
            if (!this.getLanguagePreference()) this.setLanguagePreference('hu');
        }
    }

    static applyCookieSettings(settings) {
        // Analitikai cookie-k kezelése
        if (!settings.analytics) {
            // Google Analytics kikapcsolása
            window['ga-disable-UA-XXXXXXXX-X'] = true;
            // Analytics cookie-k törlése
            Cookies.remove('_ga');
            Cookies.remove('_gid');
            Cookies.remove('_gat');
            Cookies.remove(COOKIE_KEYS.VISIT_COUNT);
            Cookies.remove(COOKIE_KEYS.FIRST_VISIT);
            Cookies.remove(COOKIE_KEYS.PAGE_VIEWS);
            Cookies.remove(COOKIE_KEYS.REFERRER);
        }

        // Marketing cookie-k kezelése
        if (!settings.marketing) {
            Cookies.remove('_fbp');
            Cookies.remove(COOKIE_KEYS.CAMPAIGN_SOURCE);
            Cookies.remove(COOKIE_KEYS.CAMPAIGN_MEDIUM);
            Cookies.remove(COOKIE_KEYS.CAMPAIGN_NAME);
        }

        // Funkcionális cookie-k kezelése
        if (!settings.functional) {
            Cookies.remove(COOKIE_KEYS.THEME);
            Cookies.remove(COOKIE_KEYS.LANGUAGE);
            Cookies.remove(COOKIE_KEYS.FONT_SIZE);
            Cookies.remove(COOKIE_KEYS.NOTIFICATION_SETTINGS);
            Cookies.remove(COOKIE_KEYS.LAST_VISITED);
        }
    }

    static resetForTesting() {
        Cookies.remove(COOKIE_CONSENT_KEY);
        Cookies.remove(COOKIE_SETTINGS_KEY);
        this.deleteCookies();
        window.location.reload();
    }

    static deleteCookies() {
        const cookies = Cookies.get();
        for (const cookie in cookies) {
            if (cookie !== COOKIE_CONSENT_KEY && cookie !== COOKIE_SETTINGS_KEY) {
                Cookies.remove(cookie);
            }
        }
    }
}

export default CookieService; 